/**
 * Utilitários de política de Inscrição Estadual (IE).
 *
 * Regra de negócio:
 *   O sistema TIM recusa contratos de entrega (fastchip=false) para clientes
 *   com IE inapta/baixada no UF. O GettelWeb replica essa validação antes de
 *   submeter, permitindo ao vendedor escolher prosseguir (bypass) mas flagando
 *   o contrato para revisão e notificando o criador + líder do time.
 *
 *   Contratos Fastchip (fastchip=true) são sempre liberados, mesmo com IE inapta.
 *
 *   Condições para BLOQUEIO (todas devem ser verdadeiras):
 *   1. Pelo menos uma linha com negociação "Aditivo"
 *   2. fastchip = false (contrato por entrega)
 *   3. Cliente não é MEI/Empresário Individual
 *   4. hasActiveInscricaoEstadual === false (snapshot da Receita)
 */

export interface IePolicyClient {
  naturezaJuridica?: string | null;
  hasActiveInscricaoEstadual?: boolean | null;
}

/**
 * Retorna true se o cliente é MEI ou Empresário Individual.
 * Esses casos são SEMPRE liberados — TIM não exige IE para MEI.
 */
export function isMeiOuIndividual(client: IePolicyClient | null | undefined): boolean {
  if (!client) return false;
  const nj = client.naturezaJuridica ?? '';
  return (
    nj.includes('2135') ||
    nj.includes('Empresário (Individual)') ||
    nj.toLowerCase().includes('mei') ||
    nj.toLowerCase().includes('micro empreendedor')
  );
}

export interface ContractLineForPolicy {
  negociacao?: string;
}

/**
 * Avalia se o contrato atual deve ser bloqueado pela regra de IE inapta.
 *
 * Retorna 'block' quando todas as condições estão presentes.
 * Retorna 'ok' quando não há restrição.
 *
 * IMPORTANTE: retorna 'ok' quando hasActiveInscricaoEstadual é undefined/null
 * (sem sync), pois não temos informação confiável — não bloqueamos à toa.
 */
export function evaluateIePolicy(
  client: IePolicyClient | null | undefined,
  fastchip: boolean,
  lines: ContractLineForPolicy[],
): 'block' | 'ok' {
  if (!client) return 'ok';
  if (fastchip) return 'ok';                            // Fastchip = não há problema de entrega
  if (isMeiOuIndividual(client)) return 'ok';            // MEI sempre liberado
  if (client.hasActiveInscricaoEstadual !== false) return 'ok'; // null/true = sem bloqueio
  const hasAditivo = lines.some(
    (l) => l.negociacao === 'Aditivo',
  );
  if (!hasAditivo) return 'ok';                          // Só bloqueia Aditivos
  return 'block';
}

/** Há quantos dias o snapshot da Receita foi feito? Retorna Infinity se nunca sincronizado. */
export function daysSinceLastCnpjSync(lastCnpjSync: string | null | undefined): number {
  if (!lastCnpjSync) return Infinity;
  const ms = Date.now() - new Date(lastCnpjSync).getTime();
  return ms / (1000 * 60 * 60 * 24);
}

export const CNPJ_SYNC_STALE_DAYS = 45;
